import React, { HtmlHTMLAttributes, useEffect } from 'react'
import { Helmet } from 'react-helmet-async';
import { removeUnmanagedMetaTags } from '../utils/metaTagCleanup';

const SITE_ORIGIN =
    process.env.REACT_APP_SITE_URL ||
    (typeof window !== 'undefined'
        ? `${window.location.protocol}//${window.location.host}`
        : 'https://www.plancrafter.tech');

interface props extends HtmlHTMLAttributes<HTMLDivElement> {
    title: any;
    desc?: any;
    keywords?: any;
    image?: any;
    url?: string;
    type?: string;
    /**
     * Explicit, content-level override for THIS page instance only (e.g. soft-404 on a
     * missing blog slug — see BlogDetails.tsx). Omit this prop entirely to defer to the
     * route-level default set globally by <RouteMeta /> (src/components/RouteMeta.tsx),
     * which is mounted once in App.tsx and covers every route including this one.
     * Do NOT pass `noindex={false}` "just to be explicit" — that would forcibly override
     * a private route back to indexable if this component is ever used on one of the
     * PRIVATE_ROUTE_PREFIXES routes. Only pass a boolean when you have a real, page-specific
     * reason to differ from the route's default.
     */
    noindex?: boolean;
    structuredData?: Record<string, any>;
}

const MetaSection: React.FC<props> = ({
    title,
    desc,
    keywords,
    image,
    url,
    type,
    noindex,
    structuredData,
}) => {
    const canonical = url
        ? (url.startsWith('http') ? url : SITE_ORIGIN + url)
        : (typeof window !== 'undefined'
            ? SITE_ORIGIN + window.location.pathname
            : SITE_ORIGIN);

    useEffect(() => {
        removeUnmanagedMetaTags([
            'meta[name="description"]',
            'link[rel="canonical"]',
            'meta[property="og:type"]',
            'meta[property="og:title"]',
            'meta[property="og:description"]',
            'meta[property="og:image"]',
            'meta[property="og:url"]',
        ]);
    }, []);

    return (
        <Helmet>
            <title>{title}</title>

            <meta name="description" content={desc || ''} />
            <meta name="keywords" content={keywords || ''} />
            {typeof noindex === 'boolean' && (
                <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />
            )}

            <link rel="canonical" href={canonical} />

            <meta property="og:title" content={title} />
            <meta property="og:description" content={desc || ''} />
            <meta property="og:image" content={image || ''} />
            <meta property="og:url" content={canonical} />
            <meta property="og:type" content={type || 'website'} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={desc || ''} />
            {image && <meta name="twitter:image" content={image} />}

            {structuredData && (
                <script type="application/ld+json" key="blog-schema">
                    {JSON.stringify(structuredData)}
                </script>
            )}
        </Helmet>
    )
}

export default MetaSection;
